import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { jobsEndpoint } from "../constants";

const Applicants = () => {
  const [applicants, setApplicants] = useState([]);
  const [jobDetails, setJobDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('token')
 
  const { jobId } = useParams();

  useEffect(() => {
    const fetchApplicants = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${jobsEndpoint}${jobId}/applicant_list`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
          }
        });
        
        // Process applicants with file counts
        const applicantsWithFiles = (response.data.applicants || []).map(applicant => ({
          ...applicant,
          cv_count: applicant.cv_files?.length || 0,
          extra_files_count: applicant.extra_files?.length || 0
        }));
        
        setApplicants(applicantsWithFiles);        
        
        // Set job details from first applicant
        if (response.data.applicants && response.data.applicants.length > 0) {
          setJobDetails({
            title: response.data.applicants[0].job_title,
            company: response.data.applicants[0].company_name
          });
        }
      } catch (err) {
        setError("Failed to load applicants.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchApplicants();
  }, [jobId]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const filteredApplicants = useMemo(() => {
    let result = [...applicants];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (applicant) =>
          applicant.job_title?.toLowerCase().includes(searchLower) ||
          applicant.company_name?.toLowerCase().includes(searchLower) ||
          applicant.application_status?.toLowerCase().includes(searchLower)
      );
    }

    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        // Handle dates
        if (sortConfig.key === 'date_updated') {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        }
        
        if (aVal < bVal) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [applicants, searchTerm, sortConfig]);

  const toggleDropdown = (applicantId) => {
    setDropdownOpen(dropdownOpen === applicantId ? null : applicantId);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setDropdownOpen(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'bg-green-100 text-green-600 border-green-400';
      case 'pending':
        return 'bg-yellow-100 text-yellow-600 border-yellow-400';
      case 'rejected':
        return 'bg-red-100 text-red-600 border-red-400';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-400';
    }
  };

  // Helper function to download file from URL with authentication
  const downloadFile = async (url, filename) => {
    try {
      const response = await axios.get('http://127.0.0.1:8000/api/jobs'+url, {
        responseType: 'blob',
        headers: {
          'Authorization': `Token ${token}`,
        }
      });
      
      // Create blob link to download
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert(`Failed to download ${filename}`);
    }
  };

  // Download single CV file
  const handleDownloadCV = async (applicant, fileIndex = 0) => {
    if (applicant.cv_files && applicant.cv_files.length > 0) {
      const file = applicant.cv_files[fileIndex];
      await downloadFile(file.url, file.name);
    }
  };

  // Download single extra file
  const handleDownloadExtraFile = async (applicant, fileIndex = 0) => {
    if (applicant.extra_files && applicant.extra_files.length > 0) {
      const file = applicant.extra_files[fileIndex];
      await downloadFile(file.url, file.name);
    }
  };

  // Download all CV files for an applicant
  const handleDownloadAllCVs = async (applicant) => {
    if (applicant.cv_files && applicant.cv_files.length > 0) {
      for (let i = 0; i < applicant.cv_files.length; i++) {
        const file = applicant.cv_files[i];
        await downloadFile(file.url, file.name);
        // Small delay between downloads
        if (i < applicant.cv_files.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
    }
  };

  // Download all extra files for an applicant
  const handleDownloadAllExtraFiles = async (applicant) => {
    if (applicant.extra_files && applicant.extra_files.length > 0) {
      for (let i = 0; i < applicant.extra_files.length; i++) {
        const file = applicant.extra_files[i];
        await downloadFile(file.url, file.name);
        // Small delay between downloads
        if (i < applicant.extra_files.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
    }
  };

  // Download all files for an applicant
  const handleDownloadAllFiles = async (applicant) => {
    const allFiles = [
      ...(applicant.cv_files || []),
      ...(applicant.extra_files || [])
    ];
    
    for (let i = 0; i < allFiles.length; i++) {
      const file = allFiles[i];
      await downloadFile(file.url, file.name);
      // Small delay between downloads
      if (i < allFiles.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
  };

  // Create dropdown menu for file downloads
  const renderFileDownloadDropdown = (applicant) => {
    const cvFiles = applicant.cv_files || [];
    const extraFiles = applicant.extra_files || [];
    
    return (
      <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-10 max-h-96 overflow-y-auto">
        <div className="py-1">
          {/* CV Files Section */}
          {cvFiles.length > 0 && (
            <>
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b">
                CV Files ({cvFiles.length})
              </div>
              {cvFiles.map((file, index) => (
                <button
                  key={`cv-${file.id}`}
                  onClick={() => downloadFile(file.url, file.name)}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 truncate"
                  title={file.name}
                >
                  📄 {file.name}
                  <span className="text-xs text-gray-500 block">
                    {file.size ? (file.size / 1024 / 1024).toFixed(2) + ' MB' : 'Size unknown'}
                  </span>
                </button>
              ))}
              {cvFiles.length > 1 && (
                <button
                  onClick={() => handleDownloadAllCVs(applicant)}
                  className="block w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 font-medium border-b"
                >
                  📥 Download All CV Files
                </button>
              )}
            </>
          )}
          
          {/* Extra Files Section */}
          {extraFiles.length > 0 && (
            <>
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b">
                Extra Files ({extraFiles.length})
              </div>
              {extraFiles.map((file, index) => (
                <button
                  key={`extra-${file.id}`}
                  onClick={() => downloadFile(file.url, file.name)}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 truncate"
                  title={file.name}
                >
                  📎 {file.name}
                  <span className="text-xs text-gray-500 block">
                    {file.size ? (file.size / 1024 / 1024).toFixed(2) + ' MB' : 'Size unknown'}
                  </span>
                </button>
              ))}
              {extraFiles.length > 1 && (
                <button
                  onClick={() => handleDownloadAllExtraFiles(applicant)}
                  className="block w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 font-medium border-b"
                >
                  📥 Download All Extra Files
                </button>
              )}
            </>
          )}
          
          {/* Download All Files */}
          {(cvFiles.length > 0 || extraFiles.length > 0) && (
            <button
              onClick={() => handleDownloadAllFiles(applicant)}
              className="block w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 font-medium border-b"
            >
              📥 Download All Files ({cvFiles.length + extraFiles.length})
            </button>
          )}
          
          {/* View Details */}
          <button
            onClick={() => navigate(`/applicant_details/${applicant.id}`)}
            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            👁️ View Details
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="lg:p-8">
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <button 
              onClick={() => navigate('/jobs')}
              className="text-gray-500 hover:text-gray-700"
            >
              ← Back to Jobs
            </button>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Applicants {jobDetails && `- ${jobDetails.title}`}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {jobDetails && `${jobDetails.company} • `}Overview of all applicants for this position.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search applicants..."
              value={searchTerm}
              onChange={handleSearch}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent"
            />
          </div>
          <button className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Export
          </button>
        </div>
      </div>

      {/* Loading/Error */}
      {loading && <p className="text-gray-500">Loading applicants...</p>}
      {error && <p className="text-red-500">{error}</p>}

      {/* Applicants Table */}
      {!loading && !error && (
        <div className="bg-white rounded-xl border border-gray-200 mt-10">
          <div className="pt-3 px-5">
            <h3 className="text-sm font-semibold text-gray-900">Total Applicants</h3>
          </div>
          <div className="flex gap-3 items-end py-4 px-5 border-b border-gray-200">
            <h3 className="text-2xl text-gray-900 font-semibold">
              {filteredApplicants.length}
            </h3>
            <div className="flex items-center gap-1 text-sm text-emerald-600">
              <span>New applications this week</span>
            </div>
          </div>

          <div>
            {/* Table Header */}
            <div className="px-5 py-3 grid md:grid-cols-8 grid-cols-5 border-b border-gray-200">
              <div className="flex gap-3 items-center md:col-span-2 col-span-2">
                <input type="checkbox" className="rounded border-gray-300" />
                <h5 
                  className="text-gray-500 font-semibold cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('id')}
                >
                  Applicant ID
                  {sortConfig.key === 'id' && (
                    <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                  )}
                </h5>
              </div>
              <div className="grid md:grid-cols-6 grid-cols-3 md:col-span-6 col-span-3">
                <h5 
                  className="text-gray-500 font-semibold text-xs cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('application_status')}
                >
                  Status
                  {sortConfig.key === 'application_status' && (
                    <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                  )}
                </h5>
                <h5 className="text-gray-500 font-semibold text-xs hidden md:block">CV</h5>
                <h5 className="text-gray-500 font-semibold text-xs hidden md:block">Candidate Documents</h5>
                <h5 className="text-gray-500 font-semibold text-xs">AI Score</h5>
                <h5 
                  className="text-gray-500 font-semibold text-xs cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('date_updated')}
                >
                  Applied Date
                  {sortConfig.key === 'date_updated' && (
                    <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                  )}
                </h5>
                <h5 className="text-gray-500 font-semibold text-xs">Actions</h5>
              </div>
            </div>

            {/* Table Rows */}
            {filteredApplicants.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p>No applicants found for this job.</p>
              </div>
            ) : (
              filteredApplicants.map((applicant) => (
                <div key={applicant.id} className="grid md:grid-cols-8 grid-cols-5 items-center p-4 border-b border-gray-200 hover:bg-gray-50">
                  <div className="flex items-center gap-3 md:col-span-2 col-span-2">
                    <input type="checkbox" className="rounded border-gray-300" />
                    <p className="md:font-medium font-bold text-xs text-gray-900">
                      #{applicant.id}
                    </p>
                  </div>
                  <div className="grid md:grid-cols-6 grid-cols-3 md:col-span-6 col-span-3 items-center">
                    <div>
                      <span className={`text-center text-[12px] border py-1 px-2 rounded-md ${getStatusBadgeColor(applicant.application_status)}`}>
                        {applicant.application_status || 'Pending'}
                      </span>
                    </div>
                    <div className="hidden md:block">
                      <span className="font-medium text-xs text-gray-500">
                        {applicant.cv_count || 0} files
                      </span>
                      {applicant.cv_count === 1 && (
                        <button
                          onClick={() => handleDownloadCV(applicant)}
                          className="ml-2 text-xs text-[#19bfb7] hover:text-[#18ada6] underline"
                        >
                          Download
                        </button>
                      )}
                      {applicant.cv_count > 1 && (
                        <span className="ml-2 text-xs text-gray-400">
                          (see menu →)
                        </span>
                      )}
                    </div>
                    <div className="hidden md:block">
                      <span className="font-medium text-xs text-gray-500">
                        {applicant.extra_files_count || 0} files
                      </span>
                      {applicant.extra_files_count === 1 && (
                        <button
                          onClick={() => handleDownloadExtraFile(applicant)}
                          className="ml-2 text-xs text-[#19bfb7] hover:text-[#18ada6] underline"
                        >
                          Download
                        </button>
                      )}
                      {applicant.extra_files_count > 1 && (
                        <span className="ml-2 text-xs text-gray-400">
                          (see menu →)
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-xs text-gray-500">--</p>
                    </div>
                    <div className="flex justify-between items-center w-full md:justify-start">
                      <span className="font-medium text-xs text-gray-500">
                        {new Date(applicant.date_updated).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex justify-end items-center">
                      <button
                        onClick={() => navigate(`/applicant_details/${applicant.id}`)}
                        className="text-[#19bfb7] hover:text-[#18ada6] text-xs font-medium mr-3"
                      >
                        View
                      </button>
                      <div className="relative">
                        <span
                          className="text-gray-600 cursor-pointer text-xl font-bold"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDropdown(applicant.id);
                          }}
                        >
                          &#8942;
                        </span>
                        {dropdownOpen === applicant.id && renderFileDownloadDropdown(applicant)}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Applicants;